import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  getGallery,
  deleteGallery,
  reorderGallery,
  type Gallery,
} from '../../features/gallery';
import { getImageUrlById } from '../../features/images';
import { DeleteConfirmModal, DashboardPageHeader } from '../../shared/ui';
import { getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';
import GalleryModal from '../../components/dashboard/GalleryModal';

const GalleryPage: React.FC = () => {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [items, setItems] = useState<Gallery[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Gallery | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Gallery | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [isReordering, setIsReordering] = useState(false);

  const canView = !!profile && can(profile, 'gallery', 'view');
  const canEdit = !!profile && can(profile, 'gallery', 'edit');
  const canDelete = !!profile && can(profile, 'gallery', 'delete');

  useEffect(() => {
    if (canView) {
      loadItems();
    }
  }, [canView]);

  const loadItems = async () => {
    try {
      const data = await getGallery();
      setItems(data);
    } catch (err) {
      logErrorInDev('Failed to load gallery items', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to load gallery items.'), 'error');
    }
  };

  const handleSaved = () => {
    showToast('Gallery item saved.');
    loadItems();
    setShowModal(false);
    setEditingItem(null);
  };

  const handleEdit = (item: Gallery) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteClick = (item: Gallery) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteGallery(itemToDelete.id);
      setItems((prev) => prev.filter((i) => i.id !== itemToDelete.id));
      setShowDeleteModal(false);
      setItemToDelete(null);
      showToast('Gallery item deleted.');
    } catch (err) {
      logErrorInDev('Failed to delete gallery item', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete gallery item.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const moveItem = async (index: number, direction: 'up' | 'down') => {
    if (isReordering) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= filteredItems.length) return;

    setIsReordering(true);
    try {
      const newItems = [...filteredItems];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];

      // Update display order locally
      const updatedItems = newItems.map((item, i) => ({
        ...item,
        displayOrder: i,
      }));
      setItems(updatedItems);

      // Send reorder request to server
      await reorderGallery({ ids: updatedItems.map((i) => i.id) });
      showToast('Gallery order saved.');
    } catch (err) {
      logErrorInDev('Failed to reorder gallery', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to save gallery order.'), 'error');
      // Reload to get correct order
      loadItems();
    } finally {
      setIsReordering(false);
    }
  };

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let filtered = items.filter((item) => {
      const matchesSearch =
        !term ||
        item.title.toLowerCase().includes(term) ||
        (item.description ?? '').toLowerCase().includes(term);
      const matchesFilter =
        filterActive === 'all' ||
        (filterActive === 'active' && item.isActive) ||
        (filterActive === 'inactive' && !item.isActive);
      return matchesSearch && matchesFilter;
    });

    // Sort by display order
    return filtered.sort((a, b) => a.displayOrder - b.displayOrder);
  }, [items, searchTerm, filterActive]);

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2"></i>
            <strong>Access Restricted:</strong> You do not have permission to view the gallery.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <DashboardPageHeader
        title="Gallery Management"
        description="Manage home page gallery carousel items."
        icon="bi bi-images"
        actions={
          canEdit ? (
            <button
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={() => {
                setEditingItem(null);
                setShowModal(true);
              }}
            >
              <i className="bi bi-plus-circle"></i>
              Add Gallery Item
            </button>
          ) : null
        }
      />

      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-6">
                  <label className="form-label text-muted small mb-1">Search</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search by title or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label text-muted small mb-1">Filter by Status</label>
                  <select
                    className="form-select"
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
                  >
                    <option value="all">All Items</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>
                <div className="col-md-2 text-md-end">
                  <span className="text-muted small">
                    {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {filteredItems.map((item, index) => (
          <div key={item.id} className="col-12 col-md-6 col-xl-4">
            <div className={`card h-100 border-0 shadow-sm ${!item.isActive ? 'opacity-75' : ''}`}>
              <div className="position-relative">
                {item.imageId ? (
                  <img
                    src={getImageUrlById(item.imageId)}
                    alt={item.title}
                    className="card-img-top"
                    style={{ height: 200, objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="card-img-top bg-light d-flex align-items-center justify-content-center"
                    style={{ height: 200 }}
                  >
                    <i className="bi bi-image text-muted" style={{ fontSize: '3rem' }}></i>
                  </div>
                )}
                <div className="position-absolute top-0 end-0 p-2">
                  <span
                    className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}
                  >
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="card-body">
                <h5 className="card-title mb-2">{item.title}</h5>
                {item.descriptionHtml ? (
                  <div
                    className="card-text small text-muted rich-text-preview mb-3"
                    dangerouslySetInnerHTML={{
                      __html: item.descriptionHtml.slice(0, 150) + (item.descriptionHtml.length > 150 ? '...' : ''),
                    }}
                  />
                ) : item.description ? (
                  <p className="card-text small text-muted mb-3">
                    {item.description.slice(0, 150)}
                    {item.description.length > 150 ? '...' : ''}
                  </p>
                ) : null}
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">Order: {item.displayOrder}</small>
                  {canEdit && (
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0 || isReordering}
                        title="Move up"
                      >
                        <i className="bi bi-arrow-up"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === filteredItems.length - 1 || isReordering}
                        title="Move down"
                      >
                        <i className="bi bi-arrow-down"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="card-footer bg-white border-top-0 d-flex justify-content-between">
                {canEdit && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleEdit(item)}
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteClick(item)}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <i className="bi bi-images text-muted" style={{ fontSize: '3rem' }}></i>
              <p className="text-muted mt-3">
                {searchTerm || filterActive !== 'all'
                  ? 'No gallery items match your search/filter.'
                  : 'No gallery items yet. Click "Add Gallery Item" to create one.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <GalleryModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onGallerySaved={handleSaved}
        item={editingItem}
        userId={user?.id ?? ''}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        itemName={itemToDelete?.title ?? ''}
        itemType="Gallery Item"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default GalleryPage;
